import React from 'react'
import PublicLayout from '../components/PublicLayout'

/**
 * Accessibility statement.
 *
 * The text mirrors docs/drafts/accessibility-statement.md (final 2026-10-16).
 * Change both together.
 *
 * ⚠️ This page is a factual claim about the product, so it must track reality.
 * Two rules for anyone editing it:
 *
 *   1. NEVER upgrade the conformance claim to "fully conformant" without an
 *      independent audit. Self-assessment cannot support that claim, and an
 *      overstated accessibility claim is itself a deceptive statement — the
 *      exact problem the "best price" copy had.
 *   2. The Known limitations section is what makes the rest credible. Remove an
 *      item only when it has actually been fixed and verified, and move it up to
 *      "What we have done" with the date.
 *
 * Typography matches Legal.jsx (16px in a 60ch column; gap analysis
 * §12). The target stays WCAG 2.1 AA (owner, 2026-10-16); moving to 2.2 means
 * checking the criteria 2.2 added first.
 */

const CONTACT = 'hello@praenum.com'
const UPDATED = 'October 16, 2026'
const PREVIOUS = 'July 29, 2026'

function H({ children }) {
  return <h2 className="text-xl font-bold text-brand-900 pt-4">{children}</h2>
}

function UL({ children }) {
  return <ul className="list-disc pl-6 space-y-2">{children}</ul>
}

export default function Accessibility() {
  return (
    <PublicLayout
      title="Accessibility"
      description="Praenum's accessibility statement: the standard we aim at, what we have tested, the gaps we know about, and how to report a barrier."
    >
      <section className="bg-gradient-to-b from-brand-100 to-white print:bg-none">
        <div className="max-w-[60ch] mx-auto px-4 pt-16 pb-8 print:pt-0">
          <h1 className="text-3xl font-extrabold tracking-tight text-brand-900">
            Accessibility
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            Last updated {UPDATED} (previous version: {PREVIOUS})
          </p>
        </div>
      </section>

      <section className="max-w-[60ch] mx-auto px-4 pb-16">
        <div className="space-y-6 text-gray-600 leading-relaxed text-base">
          <p>
            Praenum exists to help nonprofits find funding they already qualify for. That is
            worth very little if the people doing the work cannot use it. We want Praenum to be
            usable with a screen reader, a keyboard, magnification, or whatever else you rely
            on.
          </p>

          <H>Conformance status</H>
          <p>
            We aim to meet{' '}
            <a
              className="text-brand-600 underline"
              href="https://www.w3.org/TR/WCAG21/"
              target="_blank"
              rel="noopener noreferrer"
            >
              WCAG 2.1 Level AA<span className="sr-only"> (opens in a new tab)</span>
            </a>
            . Praenum is currently <strong>partially conformant</strong>: most of the standard
            is met, and we know of specific gaps, listed below.
          </p>
          <p>
            This assessment is our own. Praenum has not been audited by an independent
            accessibility firm, so we do not claim full conformance. We would rather tell you
            where we actually stand than publish a claim we cannot back.
          </p>

          <H>How we assessed it</H>
          <p>
            In July 2026 we reviewed the website and the account pages as they were then against
            WCAG 2.1, using automated checks and a review of the code, and we measured text
            colour contrast. Pages added or substantially changed since then have not had the
            same review; they are listed under "Known limitations".
          </p>

          <H>What we have done</H>
          <p>In that July 2026 review we found and fixed:</p>
          <UL>
            <li>
              Every form field now has a label a screen reader can read. Previously the labels
              were visible but not programmatically connected, which meant a screen reader
              announced sign-in and signup fields as blank.
            </li>
            <li>Errors and confirmations are announced, rather than only appearing on screen.</li>
            <li>
              Every page sets its own title, so tabs and screen-reader page lists are
              meaningful.
            </li>
            <li>A "skip to main content" link lets keyboard users bypass the navigation.</li>
            <li>
              Text colour contrast was measured and raised to meet the AA ratio, including small
              print such as pricing details and form hints.
            </li>
            <li>Headings, landmarks and page structure were corrected.</li>
          </UL>

          <H>Known limitations</H>
          <p>We would rather name these than let you discover them:</p>
          <UL>
            <li>
              <strong>Pages added since July 2026 have not been reviewed.</strong> These are
              foundation search, the search across every grantmaker, and the step after signup
              where you confirm your organization's IRS record. Funder profiles, the dashboard,
              and the signup and sign-in pages have also changed since the review.
            </li>
            <li>
              <strong>We have not completed end-to-end screen reader testing.</strong> Our
              checks were largely automated and code-level. Signup, email confirmation and the
              dashboard have not yet been walked through start to finish with VoiceOver or NVDA.
            </li>
            <li>
              <strong>Magnification and reflow are unverified.</strong> We have not confirmed
              the site works at 400% zoom without horizontal scrolling.
            </li>
            <li>
              <strong>The digest email has not had a full accessibility pass.</strong> It is
              simple HTML with headings and links and no pictures. The only image in it is the
              invisible one that reports when a digest is opened, described in our Privacy
              Policy. We have not tested it properly in screen readers.
            </li>
            <li>
              <strong>Documents we send by email have not been checked.</strong> This includes
              the PDF guide we send beta testers. Ask, and we will send the same content as plain
              text.
            </li>
            <li>
              <strong>Payment pages are operated by Stripe.</strong> Checkout and billing
              management run on Stripe's own pages, and their accessibility is outside our
              control.
            </li>
          </UL>

          <H>Using Praenum</H>
          <UL>
            <li>
              The account area is a web application and needs JavaScript. The public funding
              pages are plain HTML and work without it.
            </li>
            <li>
              Praenum relies on HTML, CSS, JavaScript and WAI-ARIA to work with assistive
              technology.
            </li>
            <li>
              We have not yet tested specific combinations of browsers and assistive technology.
            </li>
            <li>
              Every digest is sent in two versions, formatted (HTML) and plain text. Most email
              apps let you choose to read the plain-text version.
            </li>
          </UL>

          <H>Tell us when we get it wrong</H>
          <p>
            If any part of Praenum is difficult or impossible for you to use, please email{' '}
            <a href={`mailto:${CONTACT}`} className="text-brand-600 underline">
              {CONTACT}
            </a>
            . Tell us what you were trying to do, what happened, and what you are using (a
            screen reader, a browser, a phone) if you are comfortable sharing that.
          </p>
          <p>
            We aim to reply within <strong>5 business days</strong>. If something blocks you
            from using the service, tell us and we will find another way to get you the
            information in the meantime, while we fix the underlying problem.
          </p>
          <p>
            Fixes are not always instant, but accessibility reports go straight to the person
            who builds Praenum, rather than into a support queue.
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
